import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Wallet, QrCode, Copy, RefreshCcw, Search, ArrowRightLeft,
  CheckCircle2, Clock, XCircle, AlertCircle, Lock, ExternalLink, FileText, AlertTriangle,
  DollarSign, ArrowDownToLine, Download, CalendarClock, Zap, Undo2,
} from "lucide-react";
import api, { handleApiError, formatCurrency, formatDate, formatDateTime, brMonthStr,
  chaveDeOperacao, comIdempotencia } from "@/lib/api";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import CurrencyInput from "@/components/CurrencyInput";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/context/AuthContext";
import BankCombobox from "@/components/BankCombobox";
import { isBankAccountFormat, isBankAgencyFormat, isCompeFormat } from "@/data/banks";
import useTransferAlerts from "@/hooks/useTransferAlerts";

const CASHIN_STATUS_META = {
  awaiting_pix_payment: { label: "Aguardando PIX", tone: "amber", icon: Clock },
  awaiting_credit:      { label: "Aguardando crédito", tone: "amber", icon: AlertCircle },
  paid_pix_received:    { label: "PIX Pago",       tone: "emerald", icon: CheckCircle2 },
  transfers_started:    { label: "Transfers iniciadas", tone: "blue", icon: ArrowRightLeft },
  pix_deposit_amount_mismatch: { label: "Divergência de valor", tone: "red", icon: AlertCircle },
  canceled:             { label: "Cancelado",      tone: "red", icon: XCircle },
  not_sent:             { label: "Não enviado",    tone: "muted", icon: Clock },
  unknown:              { label: "Desconhecido",   tone: "muted", icon: AlertCircle },
};

const TRANSFER_STATUS_META = {
  pending:                       { label: "Pendente",          tone: "amber",   icon: Clock },
  created:                       { label: "Criada",            tone: "blue",    icon: ArrowRightLeft },
  processing:                    { label: "Processando",       tone: "blue",    icon: Clock },
  success:                       { label: "Concluída",         tone: "emerald", icon: CheckCircle2 },
  failed:                        { label: "Falhou",            tone: "red",     icon: XCircle },
  blocked_missing_bank_data:     { label: "Dados bancários incompletos", tone: "red", icon: AlertCircle },
  blocked_custody:               { label: "Em custódia",       tone: "purple",  icon: Lock },
  blocked:                       { label: "Bloqueada",         tone: "red",     icon: XCircle },
  canceled:                      { label: "Cancelada",         tone: "muted",   icon: XCircle },
  transfer_created:              { label: "Transfer criada",   tone: "blue",    icon: ArrowRightLeft },
  transfer_success:              { label: "Concluída",         tone: "emerald", icon: CheckCircle2 },
  transfer_failed:               { label: "Falhou",            tone: "red",     icon: XCircle },
  transfer_processing:           { label: "Processando",       tone: "blue",    icon: Clock },
  // C7 — o estado que o split por tipo (C3) criou: a perna foi planejada e NÃO
  // existe no provedor; ela sai sozinha na data do tipo dela. Tom próprio de
  // propósito: em "azul de processando" o operador leria como "já saiu".
  scheduled:                     { label: "Agendada",          tone: "cyan",    icon: CalendarClock },
  // RECUPERAÇÃO (fatia 2) — o desfecho TERMINAL: a empresa retomou o valor de
  // uma perna que parou e nunca saiu. Tom `muted`, ao lado de "Cancelada": não
  // é falha (nada quebrou, não há o que corrigir) nem sucesso (o colaborador
  // não recebeu). É um pagamento encerrado, e a linha não pede mais nada.
  recovered:                     { label: "Recuperada",        tone: "muted",   icon: Undo2 },
};

const REASON_META = {
  salary_payment:      "Folha",
  custody_release:     "Liberação Custódia",
  custody_blocked:     "Custódia bloqueada",
  anticipation_payout: "Repasse Antecipações",
  transfer:            "Transferência",
};

const TONE_CLASS = {
  amber:   "bg-amber-500/15 text-amber-400 border-amber-500/20",
  emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  blue:    "bg-blue-500/15 text-blue-400 border-blue-500/20",
  red:     "bg-red-500/15 text-red-400 border-red-500/20",
  purple:  "bg-purple-500/15 text-purple-400 border-purple-500/20",
  cyan:    "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  muted:   "bg-white/5 text-muted-foreground border-white/10",
};

function StatusBadge({ meta, testId }) {
  const Icon = meta?.icon || AlertCircle;
  const tone = meta?.tone || "muted";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold border ${TONE_CLASS[tone]}`}
    >
      <Icon className="w-3 h-3" /> {meta?.label || "—"}
    </span>
  );
}

// C7 — os selos do SPLIT que acompanham a linha da perna.
//
// `due_date` responde a única pergunta que uma perna "Agendada" levanta na tela
// ("agendada até quando?"), e `beneficiary_type` diz por que aquela data e não
// a outra. O selo de ATRASO (`late_release`) é carimbo Q2=(a) do owner —
// auditoria INTERNA do painel, invisível ao colaborador e sem notificação; ele
// só existe no payload das rotas de painel (a API pública não o recebe).
function SplitLegBadges({ transfer, idPrefix }) {
  const agendada = String(transfer?.status || "").toLowerCase() === "scheduled";
  const dueDate = transfer?.due_date;
  const tipo = transfer?.beneficiary_type;
  if (!agendada && !transfer?.late_release) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {agendada && dueDate && (
        <span data-testid={`${idPrefix}-due-date`}
          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-cyan-500/20 bg-cyan-500/10 text-cyan-300">
          <CalendarClock className="w-3 h-3" /> Sai em {formatDate(dueDate)}{tipo ? ` · ${tipo}` : ""}
        </span>
      )}
      {/* Anomalia REAL, não caso teórico: data ilegível no fechamento deixa a
          perna `scheduled` sem `due_date` utilizável (o rail recusa mandar num
          dia que ninguém escolheu). Sem este selo a linha ficava com um
          contêiner vazio e nenhuma pista do porquê de o pagamento não sair. */}
      {agendada && !dueDate && (
        <span data-testid={`${idPrefix}-sem-data`}
          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-red-500/20 bg-red-500/10 text-red-300">
          <AlertTriangle className="w-3 h-3" /> Agendada sem data legível — não sai sozinha
        </span>
      )}
      {transfer?.late_release && (
        <span data-testid={`${idPrefix}-late-release`} title="Uso interno: o cash-in caiu depois da data de pagamento deste tipo, e a perna saiu no ato. O colaborador não vê este selo."
          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-amber-500/20 bg-amber-500/10 text-amber-300">
          <AlertTriangle className="w-3 h-3" /> Liberada em atraso (interno)
        </span>
      )}
    </div>
  );
}

// RECUPERAÇÃO (fatia 3) — o selo do ESTORNO, e por que ele é um selo e não um
// `status`.
//
// As duas famílias que este selo cobre já compunham o saldo antes dele existir
// (o crédito está no ledger). O que faltava era a EMPRESA ver, NA PERNA, que
// aquele pagamento voltou: "Falhou" e "Concluída" são verdades incompletas
// quando o dinheiro já está de volta na conta dela.
//
// Não pode virar `status` porque nenhuma das duas famílias muda o estado da
// perna: a transfer falhada continua `failed` (é o rótulo do que o provedor
// fez) e a devolvida pós-liquidação continua `success` (o pagamento ACONTECEU —
// o Pull on Drift não toca o plano, e não deve). O selo é DERIVADO do extrato na
// leitura e anda AO LADO do status, como o "Sai em <data>" do C7.
//
// O rótulo vem do backend (`refund_label`), não de um mapa daqui: dois mapas de
// rótulo divergem na primeira edição, e aí a mesma perna diz coisas diferentes
// no painel e na API pública.
const REFUND_TONE = {
  reversed:           "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
  refunded:           "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
  partially_refunded: "border-amber-500/20 bg-amber-500/10 text-amber-300",
  // Único estado ainda EM MOVIMENTO: o crédito não chegou. Tom de espera, não
  // de erro — não há nada a corrigir, e ele some sozinho quando o crédito entra.
  reversal_pending:   "border-blue-500/20 bg-blue-500/10 text-blue-300",
};

function RefundBadge({ transfer, idPrefix }) {
  const estado = transfer?.refund_state;
  if (!estado) return null;
  const devolvido = Number(transfer?.refund_amount || 0);
  const base = transfer?.refund_base_amount;
  const emTransito = estado === "reversal_pending";
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      <span data-testid={`${idPrefix}-refund`}
        title={emTransito
          // FS-051 — o vínculo perna×crédito passou a cobrir os DOIS rails
          // (webhook e conciliação). O texto continua sem prometer "some
          // sozinho": o movimento que a conciliação não consegue correlacionar
          // fica RETIDO para revisão manual e não entra no saldo em silêncio —
          // dizer isso é o que dá ao operador o que conferir, em vez de mandá-lo
          // esperar.
          ? "A transferência falhou e o crédito ainda não chegou. O vínculo cobre os estornos recebidos por webhook e por conciliação; um movimento que a conciliação não conseguir correlacionar fica retido para revisão manual e não entra no saldo sem aviso. Confira o extrato antes de tratar como pendência."
          : base != null
            ? `${formatCurrency(devolvido)} de volta no seu saldo, de ${formatCurrency(base)} que saíram.`
            : "Devolução registrada no seu extrato."}
        className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${REFUND_TONE[estado] || REFUND_TONE.reversal_pending}`}>
        <Undo2 className="w-3 h-3" /> {transfer.refund_label}
        {!emTransito && devolvido > 0 && (
          <span className="opacity-80">
            {` · ${formatCurrency(devolvido)}${base != null ? ` de ${formatCurrency(base)}` : ""}`}
          </span>
        )}
      </span>
    </div>
  );
}

function QrCodeDialog({ open, onOpenChange, cashin }) {
  const copyPix = async () => {
    const code = cashin?.pix_emv || cashin?.pix_qr_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Código PIX copiado");
    } catch {
      toast.error("Não foi possível copiar o código PIX");
    }
  };

  if (!cashin) return null;
  const meta = CASHIN_STATUS_META[cashin.status] || CASHIN_STATUS_META.unknown;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-white/10" data-testid="qr-code-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-emerald-400" /> PIX — {cashin.month_label}
          </DialogTitle>
          <DialogDescription>Escaneie ou copie o código para pagar o fechamento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-center">
            {cashin.pix_url ? (
              <img
                src={cashin.pix_url}
                alt="QR Code PIX"
                data-testid="qr-code-image"
                className="w-56 h-56 rounded-lg border border-white/10 bg-white p-3"
              />
            ) : (
              <div className="w-56 h-56 rounded-lg border border-white/10 bg-black/20 flex items-center justify-center text-muted-foreground text-xs">
                QR Code indisponível
              </div>
            )}
          </div>
          <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase text-muted-foreground">Status</span>
              <StatusBadge meta={meta} testId="qr-dialog-status" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase text-muted-foreground">Valor</span>
              <span className="text-sm font-semibold" data-testid="qr-dialog-amount">{formatCurrency(cashin.amount)}</span>
            </div>
            {cashin.gross_up_fees > 0 && (
              <div className="space-y-1 pt-1 border-t border-white/5">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">Folha</span>
                  <span>{formatCurrency(cashin.payroll_value)}</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">Taxas ({cashin.num_transactions} transações × {formatCurrency(cashin.fee_per_transaction)})</span>
                  <span className="text-amber-400">+{formatCurrency(cashin.gross_up_fees)}</span>
                </div>
              </div>
            )}
            {cashin.pix_emv && (
              <div>
                <p className="text-[11px] uppercase text-muted-foreground mb-1">Copia e cola</p>
                <p className="text-[10px] font-mono break-all text-muted-foreground" data-testid="qr-dialog-emv">{cashin.pix_emv}</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {cashin.pix_emv && (
            <Button onClick={copyPix} data-testid="qr-dialog-copy-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              <Copy className="w-4 h-4 mr-2" /> Copiar PIX
            </Button>
          )}
          {cashin.pix_url && (
            <Button variant="outline" onClick={() => window.open(cashin.pix_url, "_blank")}
              data-testid="qr-dialog-open-btn" className="border-white/10">
              <ExternalLink className="w-4 h-4 mr-2" /> Abrir em nova aba
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TIMELINE_META = {
  done: { icon: CheckCircle2, className: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" },
  pending: { icon: Clock, className: "border-white/10 bg-white/[0.03] text-muted-foreground" },
  attention: { icon: AlertCircle, className: "border-amber-500/25 bg-amber-500/10 text-amber-300" },
  blocked: { icon: XCircle, className: "border-red-500/25 bg-red-500/10 text-red-300" },
};

function PaymentTimelineDialog({ open, onOpenChange, detail, loading, onReleased }) {
  const timeline = detail?.timeline || [];
  const transfers = detail?.transfers || [];
  // C7/Q5 — "liberar já pagamentos": MASTER-only. `usePermission` devolve
  // `canEdit` para quem tem `pagamentos: full`, e isso NÃO basta aqui: operar a
  // folha que a empresa já decidiu é uma coisa, MUDAR o dia em que ela sai é
  // outra. O colaborador (que nem chega neste painel) nunca vê nada disto.
  const { user } = useAuth();
  const { canEdit } = usePermission("pagamentos");
  const isMaster = !!user?.is_master;
  const [releasing, setReleasing] = useState(false);
  const agendadas = transfers.filter(
    (t) => String(t?.status || "").toLowerCase() === "scheduled");
  // `!loading` porque o rodapé mora FORA do ramo do spinner: sem ele, a contagem
  // e o botão do estado ANTERIOR ficavam clicáveis durante o refetch que a
  // própria liberação dispara. (`canEdit` é redundante hoje — `usePermission`
  // devolve `full` para todo master — e fica como segundo portão explícito.)
  const podeLiberar = isMaster && canEdit && !loading
    && agendadas.length > 0 && !!detail?.closing_id;

  const liberarAgora = async () => {
    if (!window.confirm(
      `Liberar agora ${agendadas.length} pagamento(s) agendado(s) de ${detail?.month_label || "este fechamento"}? ` +
      "O dinheiro sai hoje, antes da data combinada, e a ação fica registrada no seu nome.")) return;
    setReleasing(true);
    try {
      const res = await api.post(
        `/payments/closings/${detail.closing_id}/release-scheduled-now`,
        { reason: "Liberação antecipada pelo master" });
      // O lote é PARCIAL por natureza: pernas sem funding ou com pendência
      // bancária ficam retidas, e o envio de uma delas pode falhar na StarkBank.
      // Um toast verde com só o número que saiu esconderia as outras duas
      // colunas — e é justamente sobre elas que o operador tem de agir.
      const { released = 0, already_sent: already = 0, held = 0, failed = 0 } = res.data || {};
      const extras = [
        already > 0 ? `${already} já enviada(s) antes` : null,
        held > 0 ? `${held} retida(s)` : null,
        failed > 0 ? `${failed} com falha no envio` : null,
      ].filter(Boolean);
      const resumo = `${released} pagamento(s) liberado(s)`
        + (extras.length ? ` · ${extras.join(" · ")}` : "");
      if (failed > 0 || held > 0) toast.warning(resumo);
      else toast.success(resumo);
      if (onReleased) await onReleased();
    } catch (err) {
      toast.error(handleApiError(err, "Não foi possível liberar os pagamentos agendados"));
    }
    setReleasing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl border-white/10" data-testid="payment-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="payment-timeline-title">
            <FileText className="w-5 h-5 text-blue-400" /> Linha do tempo — {detail?.month_label || "Pagamento"}
          </DialogTitle>
          <DialogDescription data-testid="payment-timeline-description">
            Status operacional do PIX e dos cashouts do fechamento.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center" data-testid="payment-timeline-loading">
            <div className="loading-spinner w-6 h-6" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-5 py-2">
            <div className="space-y-3" data-testid="payment-timeline-list">
              {timeline.map((event, index) => {
                const meta = TIMELINE_META[event.status] || TIMELINE_META.pending;
                const Icon = meta.icon;
                return (
                  <div key={event.key || index} data-testid={`payment-timeline-event-${event.key || index}`}
                    className={`rounded-md border p-3 transition-colors ${meta.className}`}>
                    <div className="flex items-start gap-3">
                      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold" data-testid={`payment-timeline-event-label-${event.key || index}`}>{event.label}</p>
                          <p className="text-[11px] text-current/70" data-testid={`payment-timeline-event-date-${event.key || index}`}>{event.at ? formatDateTime(event.at) : "—"}</p>
                        </div>
                        {event.detail && <p className="text-xs text-current/75 mt-1" data-testid={`payment-timeline-event-detail-${event.key || index}`}>{event.detail}</p>}
                        {/* RECUPERAÇÃO (fatia 3) — os PARES débito×créditos, perna
                            a perna. É a única forma de a empresa conferir "voltou
                            tudo?" sem abrir o extrato e cruzar à mão: o valor que
                            SAIU ao lado do que VOLTOU, com cada lançamento. */}
                        {event.key === "cashouts_estornados" && (event.meta?.legs || []).length > 0 && (
                          <div className="mt-2 space-y-1.5" data-testid="payment-timeline-refund-legs">
                            {event.meta.legs.map((leg) => (
                              <div key={leg.external_id} data-testid={`payment-timeline-refund-leg-${leg.external_id}`}
                                className="rounded border border-current/15 bg-black/20 px-2 py-1.5">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="text-[11px] font-medium">{leg.recipient_name || leg.external_id}</span>
                                  <span className="text-[10px] uppercase tracking-wide opacity-80">{leg.refund_label}</span>
                                </div>
                                <p className="text-[11px] opacity-80">
                                  {`Saiu: ${leg.debit_amount != null ? formatCurrency(leg.debit_amount) : "—"} · Voltou: ${formatCurrency(leg.refund_amount || 0)}`}
                                </p>
                                {(leg.entries || []).map((entrada) => (
                                  <p key={entrada.key} className="text-[10px] opacity-60">
                                    {`+${formatCurrency(entrada.amount || 0)}${entrada.at ? ` em ${formatDateTime(entrada.at)}` : ""}`}
                                  </p>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-md border border-white/10 bg-black/20 p-4 space-y-4" data-testid="payment-timeline-summary">
              <div>
                <p className="text-[11px] uppercase text-muted-foreground mb-1">Valor PIX</p>
                <p className="text-lg font-bold" data-testid="payment-timeline-amount">{formatCurrency(detail?.amount || 0)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-muted-foreground mb-2">Status atual</p>
                <StatusBadge meta={CASHIN_STATUS_META[detail?.status] || CASHIN_STATUS_META.unknown} testId="payment-timeline-current-status" />
              </div>
              <div>
                <p className="text-[11px] uppercase text-muted-foreground mb-2">Cashouts</p>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="payment-timeline-transfers">
                  {transfers.length === 0 ? (
                    <p className="text-xs text-muted-foreground" data-testid="payment-timeline-no-transfers">Nenhum cashout registrado</p>
                  ) : transfers.slice(0, 12).map((transfer, index) => {
                    const meta = TRANSFER_STATUS_META[transfer.status] || TRANSFER_STATUS_META.pending;
                    const rowId = transfer.external_id || `${transfer.month}-${index}`;
                    return (
                      <div key={rowId} data-testid={`payment-timeline-transfer-${rowId}`}
                        className="flex items-center justify-between gap-3 rounded border border-white/5 bg-white/[0.02] px-2 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate" data-testid={`payment-timeline-transfer-name-${rowId}`}>{transfer.recipient_name || "Destinatário"}</p>
                          <p className="text-[11px] text-muted-foreground" data-testid={`payment-timeline-transfer-amount-${rowId}`}>{formatCurrency(transfer.amount || 0)}</p>
                          <SplitLegBadges transfer={transfer} idPrefix={`payment-timeline-transfer-${rowId}`} />
                          <RefundBadge transfer={transfer} idPrefix={`payment-timeline-transfer-${rowId}`} />
                        </div>
                        <StatusBadge meta={meta} testId={`payment-timeline-transfer-status-${rowId}`} />
                      </div>
                    );
                  })}
                  {transfers.length > 12 && (
                    <p className="text-[11px] text-muted-foreground" data-testid="payment-timeline-transfer-overflow">+{transfers.length - 12} cashout(s)</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {podeLiberar && (
          <DialogFooter className="!flex-col !items-stretch gap-2 border-t border-white/5 pt-3">
            <p className="text-[11px] text-muted-foreground" data-testid="release-scheduled-explain">
              {agendadas.length} pagamento(s) deste fechamento aguardam a data combinada e saem sozinhos nela.
              Liberar agora antecipa a saída para hoje — o cash-in precisa estar creditado, e a ação fica registrada no seu nome.
            </p>
            <Button onClick={liberarAgora} disabled={releasing} data-testid="release-scheduled-now-btn"
              className="bg-cyan-600 hover:bg-cyan-700 text-white self-end">
              {releasing ? <div className="loading-spinner" /> : <><Zap className="w-4 h-4 mr-2" /> Liberar já pagamentos</>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

const accountTypeLabel = (value) => ({
  checking: "Conta corrente",
  savings: "Conta poupança",
  salary: "Conta salário",
  CC: "Conta corrente",
  CP: "Conta poupança",
  SA: "Conta salário",
}[value] || value || "—");

function RetryTransferDialog({ open, onOpenChange, data, onConfirm, loading }) {
  const preview = data?.preview || {};
  const payment = preview.payment || {};
  const [method, setMethod] = useState(payment.method || "bank");
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [bankForm, setBankForm] = useState({
    bank_code: payment.bank_code || "",
    branch_code: payment.branch_code || "",
    account_number: payment.account_number || "",
    account_type: payment.account_type || "checking",
  });
  useEffect(() => {
    // Deriva de `data` (única dep real) — `payment` acima é só um alias de
    // data.preview.payment; referenciá-lo aqui exigiria deps redundantes.
    const p = data?.preview?.payment || {};
    setMethod(p.method || "bank");
    setSaveForFuture(false);
    setBankForm({
      bank_code: p.bank_code || "",
      branch_code: p.branch_code || "",
      account_number: p.account_number || "",
      account_type: p.account_type || "checking",
    });
  }, [data]);
  if (!data) return null;
  const canChangePayment = preview.recipient_type === "employee";
  // Banco no retry: COMPE-3 da lista oficial. Esta é a porta que gravava ISPB cru
  // em `employees.bank_code` quando o operador marca "salvar no cadastro".
  // Agência e conta seguem os espelhos EXATOS da régua canônica do backend
  // (A-R3e-xv) — o mesmo gate que `routes/payments._apply_retry_payment_override`
  // aplica antes de gravar. Aqui os dois campos são OBRIGATÓRIOS (a Transfer não
  // sai sem eles), então vazio também bloqueia, com destaque próprio abaixo.
  const agencyInvalid = !isBankAgencyFormat(bankForm.branch_code);
  const accountInvalid = !isBankAccountFormat(bankForm.account_number);
  const bankInvalid = method === "bank" && (!isCompeFormat(bankForm.bank_code) || agencyInvalid || accountInvalid || !bankForm.account_type);
  const canRetry = canChangePayment ? (method === "pix" ? !!payment.tax_id : !bankInvalid) : !!preview.can_retry;
  const missing = preview.missing_fields || [];

  const row = (label, value, testId) => (
    <div className="flex items-center justify-between gap-4 border-b border-white/5 py-2 last:border-b-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right break-all" data-testid={testId}>{value || "—"}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border-white/10" data-testid="retry-transfer-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="retry-transfer-confirm-title">
            <RefreshCcw className="w-5 h-5 text-amber-400" /> Confirmar reenvio do cashout
          </DialogTitle>
          <DialogDescription data-testid="retry-transfer-confirm-description">
            Confira os dados atuais antes de tentar criar uma nova Transfer na StarkBank.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {!canRetry && (
            <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300" data-testid="retry-transfer-blocked-alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Reenvio bloqueado</p>
                  <p className="text-xs text-red-200/80 mt-1">
                    Corrija os campos pendentes antes de tentar novamente: {missing.length ? missing.join(", ") : "dados bancários incompletos"}.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-md border border-white/10 bg-black/20 p-3" data-testid="retry-transfer-recipient-summary">
            {row("Destinatário", preview.recipient_name, "retry-transfer-recipient-name")}
            {row("CPF/CNPJ", payment.tax_id, "retry-transfer-tax-id")}
            {row("Valor", formatCurrency(preview.amount || 0), "retry-transfer-amount")}
            {row("Motivo", REASON_META[data.transfer?.reason] || data.transfer?.reason || "—", "retry-transfer-reason")}
          </div>

          <div className="rounded-md border border-white/10 bg-black/20 p-3 space-y-3" data-testid="retry-transfer-bank-summary">
            <p className="text-[11px] uppercase text-muted-foreground">Meio de pagamento do reenvio</p>
            {canChangePayment && (
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={method === "pix" ? "default" : "outline"} size="sm" data-testid="retry-payment-method-pix-button"
                  onClick={() => setMethod("pix")} className="h-8 text-xs">Chave PIX CPF/CNPJ</Button>
                <Button type="button" variant={method === "bank" ? "default" : "outline"} size="sm" data-testid="retry-payment-method-bank-button"
                  onClick={() => setMethod("bank")} className="h-8 text-xs">Conta bancária</Button>
              </div>
            )}
            {!canChangePayment ? (
              <div className="rounded border border-white/5 bg-white/[0.02] p-3" data-testid="retry-payment-locked-summary">
                <p className="text-xs text-muted-foreground">Alteração de meio de pagamento está disponível apenas para colaboradores.</p>
                {row("Banco", payment.bank_code, "retry-transfer-bank-code")}
                {row("Agência", payment.branch_code, "retry-transfer-branch-code")}
                {row("Conta", payment.account_number, "retry-transfer-account-number")}
              </div>
            ) : method === "pix" ? (
              <div className="rounded border border-blue-500/15 bg-blue-500/5 p-3" data-testid="retry-pix-summary">
                <p className="text-xs text-blue-300 font-medium">A chave PIX usada será o próprio CPF/CNPJ</p>
                <p className="text-sm mt-1" data-testid="retry-pix-key-value">{payment.tax_id || "CPF/CNPJ indisponível"}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2" data-testid="retry-bank-edit-form">
                <BankCombobox value={bankForm.bank_code} onChange={(v) => setBankForm({ ...bankForm, bank_code: v })} testId="retry-bank-code-select" placeholder="Banco" />
                {/* Valores CRUS: nenhum `onChange` reescreve o que o operador
                    digitou. O saneador que morava na conta reinterpretava o
                    último dígito como DV — no retry isso muda para onde a
                    Transfer VAI, e ainda ficava gravado no cadastro quando o
                    operador marca "salvar para próximos pagamentos". */}
                <Input data-testid="retry-bank-branch-input" value={bankForm.branch_code} onChange={(e) => setBankForm({ ...bankForm, branch_code: e.target.value })} placeholder="Agência" className={`bg-black/20 border-white/10 ${bankForm.branch_code && agencyInvalid ? "border-red-500/50" : ""}`} />
                <Input data-testid="retry-bank-account-input" value={bankForm.account_number} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} placeholder="12345-6" className={`bg-black/20 border-white/10 ${bankForm.account_number && accountInvalid ? "border-red-500/50" : ""}`} />
                <Select value={bankForm.account_type} onValueChange={(v) => setBankForm({ ...bankForm, account_type: v })}>
                  <SelectTrigger data-testid="retry-bank-account-type-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Conta corrente</SelectItem>
                    <SelectItem value="savings">Conta poupança</SelectItem>
                    <SelectItem value="salary">Conta salário</SelectItem>
                  </SelectContent>
                </Select>
                {bankForm.branch_code && agencyInvalid && <p className="col-span-2 text-[11px] text-red-400" data-testid="retry-bank-branch-error">Agência inválida. Use de 1 a 5 dígitos, sem o dígito verificador (ex.: 0001).</p>}
                {bankForm.account_number && accountInvalid && <p className="col-span-2 text-[11px] text-red-400" data-testid="retry-bank-account-error">Conta inválida. Use até 12 dígitos e o dígito verificador após um único hífen (ex.: 12345-6).</p>}
              </div>
            )}
            {canChangePayment && <div className="rounded border border-white/5 bg-white/[0.02] p-2 space-y-2" data-testid="retry-save-preference">
              <p className="text-[11px] uppercase text-muted-foreground">Como aplicar esta alteração?</p>
              <label className="flex items-center gap-2 text-xs"><input type="radio" checked={!saveForFuture} onChange={() => setSaveForFuture(false)} data-testid="retry-apply-once-radio" /> Só neste retry</label>
              <label className="flex items-center gap-2 text-xs"><input type="radio" checked={saveForFuture} onChange={() => setSaveForFuture(true)} data-testid="retry-save-future-radio" /> Salvar no cadastro para próximos pagamentos</label>
            </div>}
            {row("External ID", payment.external_id, "retry-transfer-external-id")}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="retry-transfer-cancel-button" className="border-white/10">
            Cancelar
          </Button>
          <Button onClick={() => onConfirm(canChangePayment ? { payment_method: method, ...(method === "bank" ? bankForm : {}) } : null, canChangePayment && saveForFuture)} disabled={loading || !canRetry} data-testid="retry-transfer-confirm-button"
            className="bg-amber-600 hover:bg-amber-700 text-white">
            {loading ? <div className="loading-spinner" /> : <><RefreshCcw className="w-4 h-4 mr-2" /> Confirmar reenvio</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MonthRangeFilter({ start, end, setStart, setEnd }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Label className="text-[11px] text-muted-foreground whitespace-nowrap">De</Label>
        <Input data-testid="filter-month-start" type="month" value={start}
          onChange={(e) => setStart(e.target.value)}
          className="h-8 bg-black/20 border-white/10 text-xs w-[140px]" />
      </div>
      <div className="flex items-center gap-1.5">
        <Label className="text-[11px] text-muted-foreground whitespace-nowrap">Até</Label>
        <Input data-testid="filter-month-end" type="month" value={end}
          onChange={(e) => setEnd(e.target.value)}
          className="h-8 bg-black/20 border-white/10 text-xs w-[140px]" />
      </div>
    </div>
  );
}

function CashinsTab({ monthStart, monthEnd, setMonthStart, setMonthEnd, onLedgerChanged }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionRequired, setActionRequired] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(null);
  const [qrDialog, setQrDialog] = useState(null);
  const [timelineDialog, setTimelineDialog] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      month_start: monthStart, month_end: monthEnd,
      page: String(page), page_size: "20",
    });
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (actionRequired) params.set("action_required", "true");
    if (showCanceled) params.set("include_canceled", "true");
    if (search) params.set("search", search);
    try {
      const res = await api.get(`/payments/cashins?${params}`);
      setItems(res.data.items || []);
      setPagination(res.data.pagination || null);
    } catch (err) {
      toast.error(handleApiError(err, "Erro ao carregar cash-ins"));
    }
    setLoading(false);
  }, [monthStart, monthEnd, page, statusFilter, actionRequired, showCanceled, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openTimeline = async (cashin) => {
    setTimelineDialog(cashin);
    setTimelineLoading(true);
    try {
      const res = await api.get(`/payments/cashins/${cashin.closing_id}`);
      setTimelineDialog(res.data);
    } catch (err) {
      toast.error(handleApiError(err, "Erro ao carregar linha do tempo"));
      setTimelineDialog(null);
    }
    setTimelineLoading(false);
  };

  const cancelCashin = async (cashin) => {
    if (!window.confirm(`Cancelar o PIX de ${cashin.month_label}?`)) return;
    setCanceling(cashin.closing_id);
    try {
      await api.post(`/payments/cashins/${cashin.closing_id}/cancel`, { reason: "Cancelado pela empresa pagadora" });
      toast.success("Pagamento cancelado");
      fetchData();
    } catch (err) {
      toast.error(handleApiError(err, "Não foi possível cancelar o pagamento"));
    }
    setCanceling(null);
  };

  return (
    <div className="space-y-4">
      <Card className="border-white/5">
        <CardContent className="p-4 flex flex-wrap items-center gap-3" data-testid="cashins-filters">
          <MonthRangeFilter start={monthStart} end={monthEnd}
            setStart={(v) => { setMonthStart(v); setPage(1); }}
            setEnd={(v) => { setMonthEnd(v); setPage(1); }} />
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-cashin-status" className="w-[180px] h-8 bg-black/20 border-white/10 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="awaiting_pix_payment">Aguardando PIX</SelectItem>
              <SelectItem value="awaiting_credit">Aguardando crédito</SelectItem>
              <SelectItem value="paid_pix_received">PIX Pago</SelectItem>
              <SelectItem value="transfers_started">Transfers iniciadas</SelectItem>
              <SelectItem value="pix_deposit_amount_mismatch">Divergência de valor</SelectItem>
              <SelectItem value="canceled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Button variant={actionRequired ? "default" : "outline"} size="sm"
            onClick={() => { setActionRequired((v) => !v); setPage(1); }}
            data-testid="filter-cashin-action-required"
            className={`h-8 text-xs ${actionRequired ? "bg-red-600 hover:bg-red-700 text-white" : "border-red-500/25 text-red-300 hover:bg-red-500/10"}`}>
            <AlertCircle className="w-3.5 h-3.5 mr-1.5" /> Ações necessárias
          </Button>
          <Button variant={showCanceled ? "default" : "outline"} size="sm"
            onClick={() => { setShowCanceled((v) => !v); setPage(1); }}
            data-testid="toggle-canceled-cashins-button"
            className="h-8 text-xs border-white/10">
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> {showCanceled ? "Ocultar cancelados" : "Ver cancelados"}
          </Button>
          <div className="relative flex-1 min-w-[180px] max-w-[280px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="filter-cashin-search" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por mês ou ID..."
              className="pl-9 h-8 bg-black/20 border-white/10 text-xs" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}
            data-testid="refresh-cashins-btn" className="h-8 border-white/10 text-xs ml-auto">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
          </Button>
        </CardContent>
      </Card>

      <Card className="border-white/5">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Mês</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Envio</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Depósito</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <div className="loading-spinner w-6 h-6 mx-auto" />
                  </TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="cashins-empty">
                    {actionRequired ? "Nenhum cash-in com ação necessária" : "Nenhum cash-in no período selecionado"}
                  </TableCell></TableRow>
                ) : items.map((c) => {
                  const meta = CASHIN_STATUS_META[c.status] || CASHIN_STATUS_META.unknown;
                  return (
                    <TableRow key={c.closing_id} data-testid={`cashin-row-${c.closing_id}`} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="font-medium">{c.month_label}</TableCell>
                      <TableCell><StatusBadge meta={meta} testId={`cashin-status-${c.closing_id}`} /></TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(c.amount)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.payment_sent_at ? formatDateTime(c.payment_sent_at) : "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.deposit_received_at ? formatDateTime(c.deposit_received_at) : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openTimeline(c)}
                            data-testid={`view-cashin-timeline-${c.closing_id}`}
                            className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10">
                            <FileText className="w-3.5 h-3.5 mr-1" /> Timeline
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setQrDialog(c)}
                            data-testid={`view-cashin-qr-${c.closing_id}`}
                            disabled={!c.pix_emv && !c.pix_url}
                            className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40">
                            <QrCode className="w-3.5 h-3.5 mr-1" /> Ver QR
                          </Button>
                          {c.can_cancel && (
                            <Button variant="ghost" size="sm" onClick={() => cancelCashin(c)}
                              data-testid={`cancel-cashin-${c.closing_id}`}
                              disabled={canceling === c.closing_id}
                              className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              {canceling === c.closing_id ? <div className="loading-spinner" /> : <><XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar</>}
                            </Button>
                          )}
                          {c.deposit_id && ["paid_pix_received", "transfers_started"].includes(c.status) && (
                            <Button variant="ghost" size="sm"
                              onClick={async () => {
                                try {
                                  const res = await api.get(`/payments/deposits/${c.deposit_id}/receipt`);
                                  const d = res.data;
                                  toast.success(`Depósito ${d.deposit_id}: ${formatCurrency((d.amount || 0) / 100)} — ${d.status}`, { duration: 8000 });
                                } catch (err) { toast.error(handleApiError(err, "Detalhes do depósito indisponíveis")); }
                              }}
                              data-testid={`receipt-deposit-${c.closing_id}`}
                              className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">
                              <Download className="w-3.5 h-3.5 mr-1" /> Depósito
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {pagination && pagination.total_pages > 1 && (
        <Pagination page={pagination.page} totalPages={pagination.total_pages}
          total={pagination.total} pageSize={pagination.page_size} onPageChange={setPage} />
      )}

      <QrCodeDialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)} cashin={qrDialog} />
      <PaymentTimelineDialog open={!!timelineDialog} onOpenChange={() => setTimelineDialog(null)} detail={timelineDialog} loading={timelineLoading}
        onReleased={async () => {
          if (timelineDialog?.closing_id) await openTimeline(timelineDialog);
          await fetchData();
          // O saldo do card lá em cima muda junto (a perna sai do bucket
          // "Agendado" e o saldo livre sobe) — ver `fetchBalance`.
          if (onLedgerChanged) onLedgerChanged();
        }} />
    </div>
  );
}

function TransfersTab({ monthStart, monthEnd, setMonthStart, setMonthEnd, initialStatus }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(initialStatus || "all");
  const [actionRequired, setActionRequired] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [retryConfirm, setRetryConfirm] = useState(null);
  const [changeBenefDialog, setChangeBenefDialog] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [changingBenef, setChangingBenef] = useState(false);
  const { canEdit } = usePermission("pagamentos");
  const { canEdit: canChangeBenef } = usePermission("alterar_beneficiario");

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      month_start: monthStart, month_end: monthEnd,
      page: String(page), page_size: "20",
    });
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (actionRequired) params.set("action_required", "true");
    if (showCanceled) params.set("include_canceled", "true");
    if (recipientFilter !== "all") params.set("recipient_type", recipientFilter);
    if (reasonFilter !== "all") params.set("reason", reasonFilter);
    if (search) params.set("search", search);
    try {
      const res = await api.get(`/payments/transfers?${params}`);
      setItems(res.data.items || []);
      setPagination(res.data.pagination || null);
    } catch (err) {
      toast.error(handleApiError(err, "Erro ao carregar transfers"));
    }
    setLoading(false);
  }, [monthStart, monthEnd, page, statusFilter, actionRequired, showCanceled, recipientFilter, reasonFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rowKey = (t) => `${t.month}-${t.external_id || t.recipient_id || "transfer"}`;

  const openRetryConfirm = async (t) => {
    if (!t.external_id) {
      setRetryConfirm({
        transfer: t,
        preview: {
          can_retry: false,
          recipient_name: t.recipient_name || "Destinatário",
          amount: t.amount || 0,
          missing_fields: ["external_id ausente"],
          payment: t.payment || {},
        },
      });
      return;
    }
    const key = rowKey(t);
    setPreviewing(key);
    try {
      const res = await api.post("/payments/transfers/retry/preview", { month: t.month, external_id: t.external_id });
      setRetryConfirm({ transfer: t, preview: res.data });
    } catch (err) {
      setRetryConfirm({
        transfer: t,
        preview: {
          can_retry: false,
          recipient_name: t.recipient_name || "Destinatário",
          amount: t.amount || 0,
          missing_fields: [handleApiError(err, "Não foi possível carregar a confirmação do reenvio")],
          payment: t.payment || {},
        },
      });
    }
    setPreviewing(null);
  };

  const confirmRetry = async (overridePayment, savePaymentMethod) => {
    const t = retryConfirm?.transfer;
    if (!t?.external_id) return;
    setRetrying(rowKey(t));
    try {
      const res = await api.post("/payments/transfers/retry", {
        month: t.month,
        external_id: t.external_id,
        override_payment: overridePayment,
        save_payment_method: !!savePaymentMethod,
      });
      toast.success(`Transfer re-criada (${res.data.external_id || ""})`);
      setRetryConfirm(null);
      fetchData();
    } catch (err) {
      toast.error(handleApiError(err, "Falha ao re-tentar transfer"));
    }
    setRetrying(null);
  };

  const cancelTransfer = async (t) => {
    if (!t.external_id) {
      toast.error("Pagamento sem external_id — não é possível cancelar");
      return;
    }
    if (!window.confirm(`Cancelar o cashout para ${t.recipient_name}?`)) return;
    const key = rowKey(t);
    setRetrying(key);
    try {
      const res = await api.post("/payments/transfers/cancel", { month: t.month, external_id: t.external_id, reason: "Cancelado pela empresa pagadora" });
      toast.success(res.data.provider_action === "starkbank_transfer_delete" ? "Cashout cancelado na StarkBank" : "Cashout cancelado");
      fetchData();
    } catch (err) {
      toast.error(handleApiError(err, "Não foi possível cancelar o cashout"));
    }
    setRetrying(null);
  };

  // RECUPERAÇÃO (fatia 2) — a empresa retoma o valor de uma perna que parou e
  // nunca saiu. É DEFINITIVO: por isso a confirmação nomeia a pessoa e o valor,
  // e diz o que a operação fecha. Sem isso, "Recuperar" ao lado de "Re-tentar"
  // lê-se como "tentar de outro jeito".
  const recoverTransfer = async (t) => {
    if (!t.external_id) {
      toast.error("Pagamento sem external_id — não é possível recuperar");
      return;
    }
    if (!window.confirm(
      `Recuperar ${formatCurrency(t.amount)} do pagamento de ${t.recipient_name}?\n\n` +
      "O valor volta ao saldo livre da empresa e este pagamento é ENCERRADO: " +
      "ele não poderá mais ser re-tentado. Para pagar esta pessoa depois, será " +
      "preciso incluí-la num fechamento novo.")) return;
    const key = rowKey(t);
    setRetrying(key);
    try {
      await api.post("/payments/transfers/recover", {
        month: t.month, external_id: t.external_id,
        reason: "Recuperado pela empresa pagadora",
      });
      toast.success("Pagamento recuperado — valor de volta no saldo livre");
      fetchData();
    } catch (err) {
      toast.error(handleApiError(err, "Não foi possível recuperar o pagamento"));
    }
    setRetrying(null);
  };

  const isRetryable = (t) => {
    const s = (t.status || "").toLowerCase();
    return canEdit && (
      s === "failed" || s === "blocked_missing_bank_data" ||
      s === "blocked_custody" || s === "blocked" || s === "pending" ||
      s === "transfer_failed"
    );
  };

  return (
    <div className="space-y-4">
      <Card className="border-white/5">
        <CardContent className="p-4 flex flex-wrap items-center gap-3" data-testid="transfers-filters">
          <MonthRangeFilter start={monthStart} end={monthEnd}
            setStart={(v) => { setMonthStart(v); setPage(1); }}
            setEnd={(v) => { setMonthEnd(v); setPage(1); }} />
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-transfer-status" className="w-[180px] h-8 bg-black/20 border-white/10 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="created">Criada</SelectItem>
              <SelectItem value="processing">Processando</SelectItem>
              <SelectItem value="success">Concluída</SelectItem>
              <SelectItem value="failed">Falhou</SelectItem>
              <SelectItem value="blocked_missing_bank_data">Dados bancários incompletos</SelectItem>
              <SelectItem value="blocked_custody">Em custódia</SelectItem>
              <SelectItem value="scheduled">Agendada</SelectItem>
              <SelectItem value="canceled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Button variant={actionRequired ? "default" : "outline"} size="sm"
            onClick={() => { setActionRequired((v) => !v); setPage(1); }}
            data-testid="filter-transfer-action-required"
            className={`h-8 text-xs ${actionRequired ? "bg-red-600 hover:bg-red-700 text-white" : "border-red-500/25 text-red-300 hover:bg-red-500/10"}`}>
            <AlertCircle className="w-3.5 h-3.5 mr-1.5" /> Ações necessárias
          </Button>
          <Button variant={showCanceled ? "default" : "outline"} size="sm"
            onClick={() => { setShowCanceled((v) => !v); setPage(1); }}
            data-testid="toggle-canceled-transfers-button"
            className="h-8 text-xs border-white/10">
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> {showCanceled ? "Ocultar cancelados" : "Ver cancelados"}
          </Button>
          <Select value={recipientFilter} onValueChange={(v) => { setRecipientFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-transfer-recipient" className="w-[150px] h-8 bg-black/20 border-white/10 text-xs">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="employee">Colaborador</SelectItem>
              <SelectItem value="credor">Credor</SelectItem>
            </SelectContent>
          </Select>
          <Select value={reasonFilter} onValueChange={(v) => { setReasonFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-transfer-reason" className="w-[180px] h-8 bg-black/20 border-white/10 text-xs">
              <SelectValue placeholder="Motivo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os motivos</SelectItem>
              <SelectItem value="salary_payment">Folha</SelectItem>
              <SelectItem value="custody_release">Liberação Custódia</SelectItem>
              <SelectItem value="custody_blocked">Custódia bloqueada</SelectItem>
              <SelectItem value="anticipation_payout">Repasse Antecipações</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px] max-w-[260px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="filter-transfer-search" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar destinatário ou ID..."
              className="pl-9 h-8 bg-black/20 border-white/10 text-xs" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}
            data-testid="refresh-transfers-btn" className="h-8 border-white/10 text-xs">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
          </Button>
        </CardContent>
      </Card>

      <Card className="border-white/5">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[960px]">
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Mês</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Destinatário</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Motivo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Taxa</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Atualizado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    <div className="loading-spinner w-6 h-6 mx-auto" />
                  </TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground" data-testid="transfers-empty">
                    {actionRequired ? "Nenhum cashout com ação necessária" : "Nenhuma transferência no filtro selecionado"}
                  </TableCell></TableRow>
                ) : items.map((t, idx) => {
                  const meta = TRANSFER_STATUS_META[t.status] || TRANSFER_STATUS_META.pending;
                  const rowId = t.external_id || `${t.month}-${t.recipient_id}-${idx}`;
                  const actionKey = rowKey(t);
                  return (
                    <TableRow key={rowId} data-testid={`transfer-row-${rowId}`} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="text-xs">{t.month}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${t.recipient_type === "credor" ? "bg-indigo-500/15 text-indigo-300" : "bg-slate-500/15 text-slate-300"}`}>
                            {t.recipient_type === "credor" ? "Credor" : t.recipient_type === "employee" ? "Colab." : "—"}
                          </span>
                          <span className="text-sm font-medium">{t.recipient_name || "—"}</span>
                        </div>
                        {t.error && <p className="text-[11px] text-red-400 mt-1 line-clamp-1">{t.error}</p>}
                        {(t.missing_fields || []).length > 0 && (
                          <p className="text-[11px] text-red-400/80 mt-1">Falta: {t.missing_fields.join(", ")}</p>
                        )}
                        <SplitLegBadges transfer={t} idPrefix={`transfer-${rowId}`} />
                        <RefundBadge transfer={t} idPrefix={`transfer-${rowId}`} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{REASON_META[t.reason] || t.reason || "—"}</TableCell>
                      <TableCell><StatusBadge meta={meta} testId={`transfer-status-${rowId}`} /></TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(t.amount)}</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">{t.fee > 0 ? formatCurrency(t.fee) : "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {t.updated_at ? formatDateTime(t.updated_at) : (t.created_at ? formatDateTime(t.created_at) : "—")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                        {isRetryable(t) ? (
                          <Button variant="ghost" size="sm"
                            onClick={() => openRetryConfirm(t)}
                            disabled={retrying === actionKey || previewing === actionKey}
                            data-testid={`retry-transfer-${rowId}`}
                            className="h-7 px-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10">
                            {retrying === actionKey || previewing === actionKey
                              ? <div className="loading-spinner" />
                              : <><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Re-tentar</>}
                          </Button>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                        {/* RECUPERAÇÃO (fatia 2) — `can_recover` vem do MESMO
                            predicado da rota (`_transfer_is_recoverable`), nunca
                            de uma régua da tela: um botão que aparece onde a rota
                            recusa treina o operador a ignorar a recusa. É
                            marcador INTERNO — não sai na API pública, por onde o
                            RH monta a tela do próprio colaborador. */}
                        {canEdit && t.can_recover && (
                          <Button variant="ghost" size="sm" onClick={() => recoverTransfer(t)}
                            disabled={retrying === actionKey || previewing === actionKey}
                            data-testid={`recover-transfer-${rowId}`}
                            className="h-7 px-2 text-xs text-sky-400 hover:text-sky-300 hover:bg-sky-500/10">
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Recuperar
                          </Button>
                        )}
                        {canEdit && t.can_cancel && t.status !== "canceled" && (
                          <Button variant="ghost" size="sm" onClick={() => cancelTransfer(t)}
                            disabled={retrying === actionKey || previewing === actionKey}
                            data-testid={`cancel-transfer-${rowId}`}
                            className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10">
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar
                          </Button>
                        )}
                        {canChangeBenef && t.can_cancel && t.status !== "canceled" && (
                          <Button variant="ghost" size="sm"
                            onClick={() => {
                              setChangeBenefDialog(t);
                              if (!employees.length) api.get("/employees").then(r => setEmployees(r.data || [])).catch(() => {});
                            }}
                            data-testid={`change-benef-${rowId}`}
                            className="h-7 px-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10">
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Alterar
                          </Button>
                        )}
                        {t.transfer_id && ["success", "processing", "transfer_success", "transfer_processing"].includes(t.status) && (
                          <Button variant="ghost" size="sm"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/payments/transfers/${t.transfer_id}/receipt`, { responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                const a = document.createElement("a"); a.href = url;
                                a.download = `comprovante-${t.transfer_id}.pdf`;
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                              } catch (err) { toast.error(handleApiError(err, "Comprovante indisponível")); }
                            }}
                            data-testid={`receipt-transfer-${rowId}`}
                            className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">
                            <Download className="w-3.5 h-3.5 mr-1" /> Comprovante
                          </Button>
                        )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {pagination && pagination.total_pages > 1 && (
        <Pagination page={pagination.page} totalPages={pagination.total_pages}
          total={pagination.total} pageSize={pagination.page_size} onPageChange={setPage} />
      )}
      <RetryTransferDialog
        open={!!retryConfirm}
        onOpenChange={(open) => { if (!open) setRetryConfirm(null); }}
        data={retryConfirm}
        onConfirm={confirmRetry}
        loading={!!retrying}
      />
      <ChangeBeneficiaryDialog
        open={!!changeBenefDialog}
        onOpenChange={(open) => { if (!open) setChangeBenefDialog(null); }}
        transfer={changeBenefDialog}
        employees={employees}
        loading={changingBenef}
        onConfirm={async (newEmpId) => {
          if (!changeBenefDialog) return;
          setChangingBenef(true);
          try {
            await api.post("/payments/transfers/change-beneficiary", {
              month: changeBenefDialog.month,
              external_id: changeBenefDialog.external_id,
              new_employee_id: newEmpId,
            });
            toast.success("Beneficiário alterado com sucesso");
            setChangeBenefDialog(null);
            fetchData();
          } catch (err) {
            toast.error(handleApiError(err, "Erro ao alterar beneficiário"));
          }
          setChangingBenef(false);
        }}
      />
    </div>
  );
}

function ChangeBeneficiaryDialog({ open, onOpenChange, transfer, employees, loading, onConfirm }) {
  const [selectedId, setSelectedId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => { if (open) { setSelectedId(""); setSearchTerm(""); } }, [open]);

  const filtered = (employees || []).filter((e) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (e.name || "").toLowerCase().includes(q) || (e.department || "").toLowerCase().includes(q);
  }).slice(0, 20);

  if (!transfer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-white/10 max-h-[80vh] overflow-y-auto" data-testid="change-beneficiary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-purple-400" /> Alterar Beneficiário
          </DialogTitle>
          <DialogDescription>
            Transferência de {formatCurrency(transfer.amount)} — atual: <strong>{transfer.recipient_name || "—"}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="change-benef-search" value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar colaborador..."
              className="pl-9 h-8 bg-black/20 border-white/10 text-xs" />
          </div>
          <div className="max-h-[240px] overflow-y-auto space-y-1">
            {filtered.map((emp) => (
              <button key={emp.id} type="button"
                data-testid={`benef-option-${emp.id}`}
                onClick={() => setSelectedId(emp.id)}
                className={`w-full text-left p-2.5 rounded-lg border transition-colors text-sm ${
                  selectedId === emp.id
                    ? "bg-purple-500/15 border-purple-500/30 text-purple-300"
                    : "bg-black/10 border-white/5 hover:bg-white/[0.03]"
                }`}>
                <span className="font-medium">{emp.name}</span>
                {emp.department && <span className="text-xs text-muted-foreground ml-2">{emp.department}</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">Nenhum colaborador encontrado</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancelar</Button>
          <Button data-testid="confirm-change-benef-btn"
            onClick={() => onConfirm(selectedId)}
            disabled={!selectedId || loading}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold">
            {loading ? <div className="loading-spinner" /> : "Confirmar Alteração"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompanyPayments() {
  const currentMonth = useMemo(() => brMonthStr(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "transfers" ? "transfers" : "cashins";
  const initialStatus = searchParams.get("status") || null;
  const [tab, setTab] = useState(initialTab);
  const [monthStart, setMonthStart] = useState(currentMonth);
  const [monthEnd, setMonthEnd] = useState(currentMonth);
  const [balance, setBalance] = useState(null);
  const { count: transferAlertCount, cashinCount: cashinAlertCount } = useTransferAlerts({ enabled: true });

  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");

  // IDK1-A — a chave nasce com a INTENÇÃO, não com o clique.
  //
  // 🔴 É o ponto que decide a peça. Se a chave fosse gerada dentro do `onClick`,
  // dois cliques no mesmo resgate seriam DUAS chaves e DUAS operações — que é
  // exactamente o duplo débito que a idempotência existe para impedir, agora
  // com a aparência de estar resolvido. Nascendo aqui, os dois cliques partilham
  // a chave e o segundo recebe a resposta do primeiro em vez de sacar de novo.
  //
  // Renovada quando o VALOR muda (ou o diálogo reabre): reutilizar a chave com
  // um corpo diferente devolve `422`, e o utilizador não tem como perceber que a
  // culpa é de uma chave que ele nunca vê. Valor novo = operação nova = chave nova.
  const chaveDoResgate = useRef(chaveDeOperacao());
  useEffect(() => { chaveDoResgate.current = chaveDeOperacao(); },
            [withdrawAmount, withdrawDialog]);
  const [withdrawing, setWithdrawing] = useState(false);

  // C7: virou `useCallback` porque a liberação antecipada muda exatamente o que
  // este card mostra — a perna sai de `scheduled_held` e o saldo livre sobe.
  // Buscado só no mount, o card ficava obsoleto até um reload da página.
  const fetchBalance = useCallback(() => {
    api.get("/ledger/balance").then((r) => setBalance(r.data)).catch((err) => {
      // Still show card with zero balance if API error (permissions, etc.)
      console.warn("Ledger balance fetch failed:", err?.response?.status);
      setBalance({ balance: 0, total_credits: 0, total_debits: 0 });
    });
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  // Keep URL in sync with tab state so shareable links continue working after
  // clicks on the in-page Tabs control.
  // Sync intencionalmente UNIDIRECIONAL (estado tab -> URL): TANTO `searchParams`
  // QUANTO `setSearchParams` ficam FORA das deps porque ambos mudam de identidade
  // em navegação externa da URL (react-router 7: `searchParams` é useMemo de
  // location.search e `setSearchParams` é useCallback que depende de `searchParams`).
  // Incluir qualquer um faria o efeito redisparar num deep-link externo com o
  // componente montado (ex.: ?tab=transfers&status=...) e apagar esses params antes
  // de o estado acompanhar — regressão.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (tab === "transfers" && current !== "transfers") {
      searchParams.set("tab", "transfers");
      setSearchParams(searchParams, { replace: true });
    } else if (tab === "cashins" && current === "transfers") {
      searchParams.delete("tab");
      searchParams.delete("status");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync unidirecional tab->URL; ver comentário acima
  }, [tab]);

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6" data-testid="company-payments">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="payments-title">
            <Wallet className="w-6 h-6 text-emerald-400" /> Pagamentos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Histórico de PIX de fechamento (cash-ins) e transferências a colaboradores e credores (cashouts)</p>
        </div>
      </div>

      {balance && (
        <Card className="border-white/5" data-testid="company-balance-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground tracking-wider">Saldo livre para resgate</p>
                  <p className="text-lg font-bold" data-testid="company-balance-value">{formatCurrency(balance.available_balance ?? balance.balance)}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>Saldo contábil: <span className="text-foreground font-medium">{formatCurrency(balance.balance)}</span></p>
                <p>Créditos: <span className="text-emerald-400 font-medium">{formatCurrency(balance.total_credits)}</span></p>
                <p>Débitos: <span className="text-red-400 font-medium">{formatCurrency(balance.total_debits)}</span></p>
              </div>
              {/* C7/Q8=(a): o bucket AGENDADO vem do saldo (`scheduled_held`, C6),
                  nunca somado na tela. Sem ele o "saldo livre" da esquerda cai
                  sem explicação: o dinheiro está lá, mas é folha com data.
                  RECUPERAÇÃO: o mesmo vale para `blocked_held` (folha que PAROU
                  — perna falhada ou sem dado bancário). Todo bucket que desconta
                  do saldo livre APARECE aqui; desconto anônimo manda o RH
                  procurar erro no ledger. */}
              {(balance.custody_held > 0 || balance.pending_transfers > 0 || balance.scheduled_held > 0 || balance.blocked_held > 0) && (
                <div className="text-xs space-y-0.5">
                  {balance.custody_held > 0 && (
                    <p className="flex items-center gap-1.5">
                      <Lock className="w-3 h-3 text-purple-400" />
                      <span className="text-muted-foreground">Em custódia:</span>
                      <span className="text-purple-400 font-medium" data-testid="balance-custody-held">{formatCurrency(balance.custody_held)}</span>
                    </p>
                  )}
                  {balance.pending_transfers > 0 && (
                    <p className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-amber-400" />
                      <span className="text-muted-foreground">Transfers pendentes:</span>
                      <span className="text-amber-400 font-medium" data-testid="balance-pending-transfers">{formatCurrency(balance.pending_transfers)}</span>
                    </p>
                  )}
                  {balance.scheduled_held > 0 && (
                    <p className="flex items-center gap-1.5">
                      <CalendarClock className="w-3 h-3 text-cyan-400" />
                      <span className="text-muted-foreground">Agendado (folha com data futura):</span>
                      <span className="text-cyan-400 font-medium" data-testid="balance-scheduled-held">{formatCurrency(balance.scheduled_held)}</span>
                    </p>
                  )}
                  {balance.blocked_held > 0 && (
                    <p className="flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-orange-400" />
                      <span className="text-muted-foreground">Bloqueado (folha que não saiu):</span>
                      <span className="text-orange-400 font-medium" data-testid="balance-blocked-held">{formatCurrency(balance.blocked_held)}</span>
                    </p>
                  )}
                </div>
              )}
              {(balance.available_balance ?? balance.balance) > 0 && (
                <Button variant="outline" size="sm" data-testid="request-withdrawal-btn"
                  className="h-8 text-xs border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/10 ml-auto"
                  onClick={() => { setWithdrawAmount(""); setWithdrawDialog(true); }}>
                  <ArrowDownToLine className="w-3.5 h-3.5 mr-1.5" /> Solicitar Resgate
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-black/20 border border-white/5" data-testid="payments-tabs">
          <TabsTrigger value="cashins" data-testid="tab-cashins" className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-400">
            <QrCode className="w-4 h-4 mr-2" /> Cash-ins (PIX)
            {cashinAlertCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold border border-red-500/30" data-testid="tab-cashins-badge">
                {cashinAlertCount > 99 ? "99+" : cashinAlertCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers" className="data-[state=active]:bg-blue-500/15 data-[state=active]:text-blue-400">
            <ArrowRightLeft className="w-4 h-4 mr-2" /> Cashouts (Transfers)
            {transferAlertCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold border border-red-500/30" data-testid="tab-transfers-badge">
                {transferAlertCount > 99 ? "99+" : transferAlertCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="cashins">
          <CashinsTab monthStart={monthStart} monthEnd={monthEnd} setMonthStart={setMonthStart} setMonthEnd={setMonthEnd} onLedgerChanged={fetchBalance} />
        </TabsContent>
        <TabsContent value="transfers">
          <TransfersTab monthStart={monthStart} monthEnd={monthEnd} setMonthStart={setMonthStart} setMonthEnd={setMonthEnd} initialStatus={initialStatus} />
        </TabsContent>
      </Tabs>

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawDialog} onOpenChange={setWithdrawDialog}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="withdrawal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-emerald-400" /> Solicitar Resgate
            </DialogTitle>
            <DialogDescription>
              Saldo livre: <strong className="text-emerald-400">{formatCurrency(balance?.available_balance ?? balance?.balance ?? 0)}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Valor do resgate (R$)</Label>
              <CurrencyInput data-testid="withdrawal-amount-input"
                value={withdrawAmount}
                onChange={setWithdrawAmount}
                placeholder="0,00" className="bg-black/20 border-white/10" />
            </div>
            {(balance?.custody_held > 0 || balance?.pending_transfers > 0 || balance?.scheduled_held > 0 || balance?.blocked_held > 0) && (
              <div className="rounded-md border border-amber-500/15 bg-amber-500/5 p-3 space-y-1">
                <p className="text-xs text-amber-300 font-medium">Valores retidos (não disponíveis para resgate):</p>
                {balance?.custody_held > 0 && <p className="text-xs text-muted-foreground">Em custódia: {formatCurrency(balance.custody_held)}</p>}
                {balance?.pending_transfers > 0 && <p className="text-xs text-muted-foreground">Transfers pendentes: {formatCurrency(balance.pending_transfers)}</p>}
                {balance?.scheduled_held > 0 && <p className="text-xs text-muted-foreground">Agendado (folha com data futura): {formatCurrency(balance.scheduled_held)}</p>}
                {balance?.blocked_held > 0 && <p className="text-xs text-muted-foreground">Bloqueado (folha que não saiu): {formatCurrency(balance.blocked_held)}</p>}
              </div>
            )}
            <div className="rounded-md border border-white/10 bg-black/20 p-3 space-y-1">
              <p className="text-[11px] uppercase text-muted-foreground">Processamento</p>
              <p className="text-xs text-muted-foreground">O resgate será processado automaticamente via PIX para o CNPJ da empresa cadastrado na plataforma.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawDialog(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-withdrawal-btn"
              disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > (balance?.available_balance ?? balance?.balance ?? 0)}
              onClick={async () => {
                const maxAvailable = balance?.available_balance ?? balance?.balance ?? 0;
                const amt = parseFloat(withdrawAmount);
                if (!amt || amt <= 0) { toast.error("Informe um valor válido"); return; }
                if (amt > maxAvailable) { toast.error(`Saldo livre insuficiente. Disponível: ${formatCurrency(maxAvailable)}`); return; }
                setWithdrawing(true);
                try {
                  // ⚠️ BURACO DECLARADO, e está aqui de propósito.
                  // O desfazer mais provável desta linha é alguém apagar o
                  // terceiro argumento — e HOJE NADA O DETECTA: este frontend
                  // não tem infra de teste de componente nem ensaio ponta a
                  // ponta. A asserção que o re-acoplaria seria «este clique
                  // envia `Idempotency-Key`», e ela não existe.
                  // NÃO se resolve procurando esta string no ficheiro: um
                  // oráculo que casa texto morre com o primeiro conserto que
                  // reescreva a linha, e o mutante que preserva o texto
                  // sobrevive (OC-REQ-1379, ponto 5).
                  // A guarda de EFEITO que existe hoje está no servidor: sem a
                  // chave, a rota recusa com 428.
                  const res = await api.post("/ledger/withdraw", { amount: amt },
                                             comIdempotencia(chaveDoResgate.current));
                  toast.success(res.data.message || "Resgate processado com sucesso!");
                  setWithdrawDialog(false);
                  // Refresh balance from API for accurate data
                  api.get("/ledger/balance").then((r) => setBalance(r.data)).catch(() => {});
                } catch (err) {
                  toast.error(handleApiError(err, "Erro ao solicitar resgate"));
                }
                setWithdrawing(false);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              {withdrawing ? <div className="loading-spinner" /> : "Confirmar Resgate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Exported for unit-testing/re-use
export { CASHIN_STATUS_META, TRANSFER_STATUS_META, QrCodeDialog };
